import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { getTitle } from '../../core-utils/helper-utils/reportsTable';
import {
  Breadcrumbs,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useStores } from '../../stores';
import { useNavigate } from 'react-router-dom';
import ComplianceReportsTable from '../../_components/organisms/ComplianceReportsTable';
import { observer } from 'mobx-react';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import properties from '../../core-utils/properties';
import axios from 'axios';
import moment from 'moment';
import Snackbar from '../../_components/atoms/Snackbar';
import { handleDownloadReport } from '../../core-utils/helper-utils/contractReportHelper';
import Loader from '../../_components/molecules/Loader';
import componentConstants from '../../core-utils/messages/componentConstants';
import { base64toByteArrayConverter } from '../../core-utils/helper-utils/downloadPdfHelper';
import { arrayToString } from '../../core-utils/helper-utils/helper';

const useStyles = makeStyles()({
  root: {
    margin: '40px 0px 66px 63px',
    flexDirection: 'column',
    width: '1186px',
  },
  breadCrumbsGrid: {
    marginBottom: '20px',
  },
  tableCard: {
    marginTop: '28px',
  },
});

const ComplianceReports = observer(() => {
  //Constants
  const { classes } = useStyles();
  const navigate = useNavigate();
  const employerGroupStore = useStores().employerGroupStore;
  const { employer_group_name } = employerGroupStore.employerGroupResponseData;
  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const breadCrumpItemList = [
    [
      'text',
      'Compliance Reports',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(-1);
      },
    ],
    [
      'text',
      'Reports',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const SPDReports = [
    'ERISA Regulatory Compliance Check',
    'Comparison with Administrative Service Agreement',
    'Comparison with PBM Service Agreement',
    'Gap Analysis',
  ];

  //States
  const [reportsTableData, setReportsTableData] = useState<any[]>([]);
  const [selectedReports, setSelectedReports] = useState<any[]>([]);
  const [loading, setPageLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);
  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  //Functions
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const getReportType = (reportName: string) => {
    switch (reportName) {
      case 'Comparison with Administrative Service Agreement':
        return 'spd-asa';
      case 'Gap Analysis':
        return 'pd-stoploss';
      case 'Comparison with PBM Service Agreement':
        return 'spd-pbm';
      default:
        return '';
    }
  };

  const getReportIdentifier = (reportName: string) => {
    switch (reportName) {
      case 'Gag Clause Prohibition Compliance Check':
        return 'gagClauseReport';
      case 'Provision Matrix':
        return 'provisionMatrixReport';
      case 'Compensation Transparency Compliance Check':
        return 'compensationTransparencyReport';
      case 'Comparison with Administrative Service Agreement':
      case 'Comparison with PBM Service Agreement':
      case 'Comparison with Stop Loss Agreement':
        return 'interContractReport';
      case 'Commercial Evaluation':
        return 'commercialAnalysisReport';
      case 'ERISA Regulatory Compliance Check':
        return 'regulatoryReport';
      case 'Gap Analysis':
        return 'gapAnalysisReport';
      case 'Red Flag Review':
        return 'redFlagsReport';
      case 'Missing Provisions Review':
        return 'missingProvisionsReport';
      default:
        return '';
    }
  };

  const modifiedReportName = (report: string) => {
    switch (report) {
      case 'Provision Matrix':
        return 'Provision Favorability Check';
      case 'Red Flag Checks':
        return 'Red Flag Review';
      case 'Missing Checks':
        return 'Missing Provisions Review';
      default:
        return report;
    }
  };

  const formatSelectedReports = (data: any) => {
    const contractMap = new Map();

    data
      ?.map((item: any) => item.original)
      ?.map((item: any) => {
        const { contractId, planId, subRows } = item;
        const matchingContract = reportsTableData?.find(
          (doc: any) =>
            (contractId && doc.contractId === contractId) ||
            (planId && doc.planId === planId),
        );
        const id = contractId || planId;

        if (!contractMap.has(id)) {
          contractMap.set(id, {
            contract_id: id,
            name: matchingContract?.description,
            vendor: matchingContract.name,
            effective_date: matchingContract?.effectiveDate,
            reports: [],
          });
        }

        const object = contractMap.get(id);

        if (subRows?.length > 0) {
          // If contract has subRows, replace any existing non-subRow entries.
          contractMap.set(id, {
            ...object,
            reports: subRows?.map((report: any) => ({
              title: report?.name,
              reportName: report?.report,
              ...(report?.compare_agreement_id && {
                compare_agreement_id: report.compare_agreement_id,
              }),
              ...(report?.type && { type: report?.type }),
            })),
          });
        } else if (!item.subRows || item?.subRows?.length === 0) {
          // If no subRows, add it to the subRows list
          const existingTitles = new Set(
            object?.reports.map((report: any) => report.title),
          );

          if (!existingTitles.has(item?.name)) {
            object?.reports.push({
              title: item?.name,
              reportName: item?.report,
              ...(item?.compare_agreement_id && {
                compare_agreement_id: item.compare_agreement_id,
              }),
              ...(item?.type && { type: item?.type }),
            });
          }
        }
      });

    return Array.from(contractMap.values());
  };

  const handleDownloadAll = () => {
    handleOpenSnackBar('All reports download in progress ', 'info', 4000);
    const documents = reportsTableData
      ?.filter((item: any) => item?.subRows?.length > 0)
      ?.map((item: any) => {
        return {
          contract_id: item?.contractId || item?.planId,
          name: item?.description,
          effective_date: item?.effectiveDate,
          vendor: item?.name,
          reports: item?.subRows?.map((report: any) => {
            return {
              title: report?.name,
              reportName: report?.report,
              ...(report?.compare_agreement_id && {
                compare_agreement_id: report.compare_agreement_id,
              }),
              ...(report?.type && {
                type: report?.type,
              }),
            };
          }),
        };
      });
    const requestBody = {
      employer: employer_group_name[0],
      eg_nid: egNid,
      documents: documents,
    };
    downloadComprehensiveReport(requestBody);
  };

  const handleDownloadSelected = () => {
    handleOpenSnackBar('Selected reports download in progres ', 'info', 4000);
    const documents = formatSelectedReports(selectedReports);

    const requestBody = {
      employer: employer_group_name[0],
      eg_nid: egNid,
      documents: documents,
    };
    downloadComprehensiveReport(requestBody);
  };

  const handleOnHoverReportDownload = (row: any) => {
    const reportId = getReportIdentifier(row?.original?.name as string);
    handleDownloadReport(
      reportId,
      employerGroupStore?.employerGroupResponseData?.employer_group_name[0],
      row?.original?.contractId || row?.original?.planId,
      handleOpenSnackBar,
      employerGroupStore?.employerGroupResponseData?.eg_nid[0],
      row?.original?.compare_agreement_id,
      getReportType(row?.original?.name),
    );
    handleOpenSnackBar('Report download is in progress', 'info', 4000);
  };

  //API Calls
  const getAllContracts = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContracts,
        {
          eg_nid: egNid,
        },
      )
      .then(async (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          const formattedData = (
            await Promise.all(
              data
                ?.filter((contract: any) => contract?.reports?.length > 0)
                .map(async (item: any) => {
                  const reportsData = await getContractReports(
                    item?.contract_id,
                  );

                  // if (
                  //   !reportsData?.find(
                  //     (report: any) =>
                  //       report?.name === 'Provision Favorability Check',
                  //   ) &&
                  //   item?.features?.includes('Provision Matrix')
                  // ) {
                  //   reportsData?.push({
                  //     contractId: item?.contract_id,
                  //     name: modifiedReportName('Provision Matrix'),
                  //     description: '',
                  //     report: getReportIdentifier('Provision Matrix'),
                  //     lastUpdated: '',
                  //   });
                  // }

                  // if (item?.features?.includes('Red Flag Checks')) {
                  //   reportsData?.push({
                  //     contractId: item?.contract_id,
                  //     name: modifiedReportName('Red Flag Checks'),
                  //     description:
                  //       'Reviews contractual provisions; flags fiduciary and compliance risks; highlights remediation and negotiation opportunities to protect plan sponsor interests.',
                  //     report: getReportIdentifier('Red Flag Review'),
                  //     lastUpdated: '',
                  //   });
                  // }

                  // if (item?.features?.includes('Missing Checks')) {
                  //   reportsData?.push({
                  //     contractId: item?.contract_id,
                  //     name: modifiedReportName('Missing Checks'),
                  //     description:
                  //       'Evaluates missing provisions; identifies potential long-term governance and compliance constraints.',
                  //     report: getReportIdentifier('Missing Provisions Review'),
                  //     lastUpdated: '',
                  //   });
                  // }

                  return (
                    reportsData?.length > 0 && {
                      id: item?.contract_id,
                      name: item?.health_care_partner_name,
                      description: item?.service_agreement_name,
                      lastUpdated: '-',
                      effectiveDate: moment(item?.effective_date).format(
                        'DD MMM YYYY',
                      ),
                      contractId: item?.contract_id,
                      subRows: reportsData,
                    }
                  );
                }),
            )
          ).filter(Boolean);
          setReportsTableData(formattedData);
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      });
  };

  const getContractReports = async (id: string) => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContractReports,
        { contract_id: id },
      );

      if (response.status === 200) {
        const data = response?.data?.data || [];
        return data
          .filter((item: any) => item?.status === 'Ready')
          .map((item: any) => ({
            id: item?._id,
            contractId: item?.contract_id,
            name: modifiedReportName(item?.report_name),
            description: item?.description,
            compare_agreement_id: item?.compare_agreement_id,
            report: getReportIdentifier(item?.report_name),
            type:
              (item?.report_name ===
                'Comparison with Administrative Service Agreement' ||
                item?.report_name === 'Comparison with Stop Loss Agreement') &&
              'asa-stoploss',
            lastUpdated: moment(item?.updatedAt).format('YYYY-MM-DD'),
          }));
      }

      return [];
    } catch (error: any) {
      handleOpenSnackBar(
        error.response?.data?.error || 'Failed to fetch reports',
        'error',
        6000,
      );
      return [];
    }
  };

  const getPlanDocuments = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getPlanDocs,
        {
          eg_nid: egNid,
          doc_type: 'SPD',
        },
      )
      .then(async (res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data || [];
          const formattedData = (
            await Promise.all(
              data
                ?.filter((plan: any) => plan?.reports?.length > 0)
                ?.map(async (item: any) => {
                  const reportsData = await getSPDReports(item?._id);
                  return (
                    reportsData?.length > 0 && {
                      planId: item?._id,
                      name: arrayToString(item?.plan_names),
                      effectiveDate: moment(item?.effective_date).format(
                        'DD MMM YYYY',
                      ),
                      description: 'Summary Plan Document',
                      lastUpdated: '-',
                      subRows: reportsData,
                    }
                  );
                }),
            )
          )?.filter(Boolean);
          setReportsTableData((prev) => [...prev, ...formattedData]);
          setPageLoading(false);
        }
      })
      .catch((error) => {
        throw error;
      });
  };

  const getSPDReports = async (planId: string) => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl + properties.contractBaseUrl + '/get-spd-reports',
        {
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          plan_id: planId,
        },
      );

      if (response.status === 200) {
        const data = response?.data?.data || [];
        return data
          .filter(
            (item: any) =>
              item?.status === 'Ready' &&
              SPDReports?.includes(item?.report_name),
          )
          .map((item: any) => ({
            planId: item?.plan_doc_id,
            name: item?.report_name,
            description: item?.description,
            report: getReportIdentifier(item?.report_name),
            compare_agreement_id: item?.compare_agreement_id,
            lastUpdated: moment(item?.updatedAt).format('YYYY-MM-DD'),
            type: getReportType(item?.report_name),
          }));
      }

      return [];
    } catch (error: any) {
      handleOpenSnackBar(
        error.response?.data?.error || 'Failed to fetch reports',
        'error',
        6000,
      );
      return [];
    }
  };

  const downloadComprehensiveReport = (requestBody: any) => {
    const token = JSON.parse(localStorage.getItem('token-storage') as string)
      ?.access_token;
    const messages =
      componentConstants.ORGANISMS.REPORTS_MODAL.SNACKBAR_MESSAGES;
    handleOpenSnackBar(messages.DOWNLOAD_REPORT, 'info', 5000);
    axios
      .post(
        properties.aiqBaseUrl + properties.downloadComprehensiveReport,
        requestBody,
        {
          headers: {
            Authorization: token,
          },
        },
      )
      .then((res) => {
        if (res) {
          const byteArray = base64toByteArrayConverter(res.data?.data);
          const file = new Blob([byteArray], {
            type: 'application/pdf;base64',
          });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, '_blank');
          handleOpenSnackBar(messages.OPEN_SUCCESS, 'success', 5000);
        }
      })
      .catch((error) => {
        handleOpenSnackBar(
          error.response?.data?.error ?? messages.API_ERROR,
          'error',
          5000,
        );
      });
  };

  const APICalls = async () => {
    await getAllContracts();
    await getPlanDocuments();
  };

  //UseEffects
  useEffect(() => {
    if (!egNid) {
      return;
    }
    APICalls();
  }, [egNid]);

  return (
    <>
      {loading ? (
        loader
      ) : (
        <>
          <Grid className={classes.root}>
            <Grid className={classes.breadCrumbsGrid}>
              <Breadcrumbs itemList={breadCrumpItemList} />
            </Grid>
            <Grid>
              <PageTitleAndSubTitle
                title={getTitle(
                  employerGroupStore.employerGroupResponseData
                    .employer_group_name[0],
                )}
                subTitle={
                  'Download your commercial evaluation and compliance check reports all at one place.'
                }
                subTitleStyles={{ width: '100%' }}
              />
            </Grid>
            <Grid className={classes.tableCard}>
              <ComplianceReportsTable
                reportsData={reportsTableData}
                handleDownloadAll={handleDownloadAll}
                handleDownloadSelected={handleDownloadSelected}
                setSelectedReports={setSelectedReports}
                showDownloadSelected={selectedReports?.length > 0}
                handleRowDownloadReport={handleOnHoverReportDownload}
              />
            </Grid>
          </Grid>
          <Snackbar
            {...snackbarState}
            vertical={'bottom'}
            horizontal={'right'}
            handleClose={handleCloseSnackBar}
          />
        </>
      )}
    </>
  );
});

export default ComplianceReports;
